/* eslint-disable react/require-default-props */
import React, {
  forwardRef, useEffect, useState, useRef, useCallback
} from 'react';
import proptypes from 'prop-types';
const { number } = proptypes;
import thdCustomer from '@thd-olt-global/thd-customer';
import B2bBubble from "./B2bBubble";
import B2cBubble from "./B2cBubble";
import { EVENTS, AUDIENCE_TYPE } from '../utils/constants';
import { hideBubbleForInterrupter } from '../utils/utils';

const TEST_PRO_BUBBLE_SESSION_KEY = 'testProBubble';

const isTestModeEnabled = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }

  try {
    const storedValue = window.sessionStorage.getItem(TEST_PRO_BUBBLE_SESSION_KEY);

    if (typeof storedValue !== 'string') {
      return false;
    }

    const normalizedValue = storedValue.trim().toLowerCase();
    return normalizedValue === 'true' || normalizedValue === '1';
  } catch (error) {
    return false;
  }
};

const SignInBubble = forwardRef(({ time = 10000 }, ref) => {

  const [open, togglePopover] = useState(false);
  // audienceType lifecycle:
  // null => not yet determined
  // AUDIENCE_TYPE.B2B or AUDIENCE_TYPE.B2C => ready to render
  const [audienceType, setAudienceType] = useState(null);
  const closeTimeoutIdRef = useRef(null);
  const scrollYRef = useRef(0);
  const audienceTimeoutRef = useRef(null);
  const [testModeEnabled] = useState(() => isTestModeEnabled());


  const closePopover = () => togglePopover(false);

  function openPopover() {
    togglePopover(true);
  }

  const closeOnScroll = useCallback(() => {
    if (Math.abs(window.scrollY - scrollYRef.current) >= 20) {
      closePopover();
    }
  }, []);


  const shouldShowSignInReminder = () => {
    if (testModeEnabled) {
      openPopover();
      return;
    }

    try {
      const executionTimestamp = Date.now();
      const bubbleTimestamp = localStorage.getItem('signInReminderTimestamp');
      const lastReminderTimestamp = Number(bubbleTimestamp);
      const THIRTY_MINUTES = 1800000;
      const TEN_SECONDS = 10000;
      
      // Only proceed once audienceType is identified and interrupter conditions are checked
      if (audienceType === null || hideBubbleForInterrupter()) return;

      if (!bubbleTimestamp && (!thdCustomer?.isLoggedIn || thdCustomer?.isPersisted)) {
        // There is no record of the SignIn Bubble Timestamp and the user is not logged in or the user is persisted.
        openPopover();
        localStorage.setItem('signInReminderTimestamp', executionTimestamp);
      } else if (((executionTimestamp - lastReminderTimestamp) < TEN_SECONDS)
        && (!thdCustomer?.isLoggedIn || thdCustomer?.isPersisted)) {
        // The reminder has been shown for less than 10 seconds
        openPopover();
        localStorage.setItem('signInReminderTimestamp', executionTimestamp);
      } else if (((executionTimestamp - lastReminderTimestamp) > THIRTY_MINUTES)
        && (!thdCustomer?.isLoggedIn || thdCustomer?.isPersisted)) {
        // The last time we have shown the reminder has exceed 30 minutes and the user is not logged in or KMSI
        openPopover();
        localStorage.setItem('signInReminderTimestamp', executionTimestamp);
      }
    } catch (error) {
      // console.log("Something went wrong trying to retrieve the timestamp")
    }
  };

  useEffect(() => {
    scrollYRef.current = window.scrollY;

    if (audienceType !== null) {
      return undefined;
    }

    if (testModeEnabled) {
      setAudienceType(AUDIENCE_TYPE.B2B);
      return () => {};
    }

    if (!window.LIFE_CYCLE_EVENT_BUS) {
      // eslint-disable-next-line no-console
      console.error('life cycle event bus was not found, unable to setup trigger for Sign In Bubble');
      setAudienceType(AUDIENCE_TYPE.B2C); // default fallback
      return () => {};
    }

    window.LIFE_CYCLE_EVENT_BUS.trigger(EVENTS.BUBBLE_READY);

    const handlePersonalizationEvent = () => {
      clearTimeout(audienceTimeoutRef.current);
      window.LIFE_CYCLE_EVENT_BUS.off(EVENTS.AUDIENCE_IDENTIFIED, handlePersonalizationEvent);
      setAudienceType((prev) => (prev === null ? AUDIENCE_TYPE.B2B : prev));
    };

    window.LIFE_CYCLE_EVENT_BUS.on(EVENTS.AUDIENCE_IDENTIFIED, handlePersonalizationEvent);

    audienceTimeoutRef.current = setTimeout(() => {
      setAudienceType((prev) => {
        if (prev !== null) {
          return prev;
        }
        return AUDIENCE_TYPE.B2C;
      });
    }, 1500); // 1.5 second timeout

    return () => {
      clearTimeout(audienceTimeoutRef.current);
      if (window.LIFE_CYCLE_EVENT_BUS) {
        window.LIFE_CYCLE_EVENT_BUS.off(EVENTS.AUDIENCE_IDENTIFIED, handlePersonalizationEvent);
      }
    };
  }, [audienceType, testModeEnabled]);

  useEffect(() => {
    if (audienceType !== null) {
      shouldShowSignInReminder();
    }
  }, [audienceType, testModeEnabled]);

  useEffect(() => {
    if (open) {
      const timeoutDuration = audienceType === AUDIENCE_TYPE.B2B ? time * 2 : time;
      closeTimeoutIdRef.current = setTimeout(closePopover, timeoutDuration);
      window.addEventListener('scroll', closeOnScroll);
      return;
    }
    clearTimeout(closeTimeoutIdRef.current);
    window.removeEventListener('scroll', closeOnScroll);
  }, [open, audienceType, time]);

  if (audienceType !== null) {
    return audienceType === AUDIENCE_TYPE.B2B
      ? <B2bBubble ref={ref} closePopover={closePopover} open={open} />
      : <B2cBubble ref={ref} closePopover={closePopover} open={open} />;
  }

  return null;

  });

SignInBubble.propTypes = {
  time: number
};

SignInBubble.displayName = 'SignInBubble';

export { SignInBubble };